import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import ActionHeader from "../../../components/ActionHeader";
import BaseTable from "../../../components/BaseTable";
import usePageState from "../../../hooks/usePageState";
import { useState } from "react";
import useModal from "../../../hooks/useModal";
import { SaleInfoModel } from "../../../../infrastructure/model/SaleInfoModel";
import { isEmpty } from "lodash";
import { useItemsContext } from "../../../context/AppItemsContext";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import useConfirmAction from "../../../hooks/useConfirmAction";
import { Image, Switch, Tag, Tooltip } from "antd";
import {
  LIMIT_PER_PAGE,
  SALE_TYPE_ENUM,
  SALE_VALUE_TYPE_OPTION,
} from "../../../../applications/constants/AppConst";
import {
  activeSaleUseCase,
  deleteSaleUseCase,
  getAllSaleInfoUseCase,
} from "../../../../applications/useCase/ApiUseCase/SaleApiUseCase";
import { formatRecurringDays } from "../../../../applications/utils/TimeUtils";
import { useSaleInfoContext } from "../../../context/AppSaleInfoContext";
import CreateSaleInfoDialog from "./CreateSaleInfoDialog";

function saleInfo() {
  const { saleInfo, setSaleInfo } = useSaleInfoContext();
  const { items } = useItemsContext();
  const { isLoading, setLoading, showSuccess, showError } = usePageState();
  const [createModal, openCreateModal] = useModal();
  const [selectedItem, setSelectedItem] = useState<SaleInfoModel>();

  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm(params) {
      handleDeleteItem();
    },
  });

  async function getAllsaleInfo() {
    const res = await getAllSaleInfoUseCase({
      page: 1,
      limit: LIMIT_PER_PAGE,
      setLoading,
      showError,
    });
    if (res) {
      setSaleInfo(res.data);
    }
  }

  async function handleDeleteItem() {
    if (!selectedItem?.id) return;

    const res = await deleteSaleUseCase({
      saleId: selectedItem.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setSaleInfo(saleInfo.filter((e) => e.id !== selectedItem.id));
      setSelectedItem(undefined);
    }
  }

  async function handleEnableActiveStatus() {
    if (!selectedItem?.id) return;
    // 1. Kiểm tra thời gian hợp lệ
    const now = Date.now();
    if (
      !(selectedItem.sale_start_date < now && now < selectedItem.sale_end_date) &&
      !selectedItem.is_recurring &&
      selectedItem.is_active === false
    ) {
      showError("Chương trình khuyến mãi không nằm trong thời gian hiệu lực");
      return;
    }

    // 2. Kiểm tra giới hạn người dùng áp dụng
    if (
      selectedItem.applicable_user_count > 0 &&
      selectedItem.applied_user_count >= selectedItem.applicable_user_count
    ) {
      showError("Số lượng người dùng áp dụng đã đạt giới hạn");
      return;
    }
    const res = await activeSaleUseCase({
      saleId: selectedItem.id,
      status: !selectedItem.is_active,
      showError,
      setLoading,
      showSuccess,
    });
    if (res) {
      const updatedItem = {
        ...selectedItem,
        is_active: !selectedItem.is_active,
      };
      const indexUpdate = saleInfo.findIndex((e) => e.id === selectedItem.id);
      setSaleInfo([
        ...saleInfo.slice(0, indexUpdate),
        updatedItem,
        ...saleInfo.slice(indexUpdate + 1),
      ]);
      setSelectedItem(updatedItem);
    }
  }

  async function handleOpenCreateDialog(updateItem?: SaleInfoModel) {
    const result = await openCreateModal<SaleInfoModel>((close) => {
      return <CreateSaleInfoDialog onClose={close} updateItem={updateItem} />;
    });
    if (result && result.id) {
      if (updateItem) {
        const indexUpdate = saleInfo.findIndex((e) => e.id === updateItem.id);
        setSaleInfo([
          ...saleInfo.slice(0, indexUpdate),
          result,
          ...saleInfo.slice(indexUpdate + 1),
        ]);
        setSelectedItem(result);
      } else {
        setSaleInfo([result, ...saleInfo]);
      }
    }
  }

  useAsyncEffect(async () => {
    await getAllsaleInfo();
  }, []);

  return (
    <div className="h-100 flex flex-column">
      <ActionHeader
        actions={[
          {
            icon: <ReloadOutlined />,
            isNormal: true,
            userClick: getAllsaleInfo,
            type: "primary",
          },
          {
            icon: <PlusCircleOutlined />,
            isNormal: true,
            userClick: handleOpenCreateDialog,
            type: "primary",
          },
          {
            icon: <EditOutlined />,
            isNormal: true,
            userClick: () => handleOpenCreateDialog(selectedItem),
            disabled: !selectedItem?.id,
            type: "primary",
          },
          {
            icon: (
              <Tooltip
                placement="top"
                title={selectedItem?.is_active ? "Tắt khuyến mãi" : "Bật khuyến mãi"}
              >
                <Switch
                  checked={selectedItem?.is_active || false}
                  onChange={() => handleEnableActiveStatus()}
                  disabled={!selectedItem?.id}
                />
              </Tooltip>
            ),
          },
          {
            isNormal: true,
            userClick: confirmDelete,
            icon: <DeleteOutlined />,
            disabled: !selectedItem?.id,
            color: "danger",
            variant: "outlined",
          },
        ]}
      />
      <BaseTable
        dataSource={saleInfo}
        columns={[
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 50,
          },
          {
            title: "Trạng thái",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render(value, record, index) {
              return (
                <Tag color={value ? "#108ee9" : "#f50"}>
                  {value ? "Hoạt động" : "Không hoạt động"}
                </Tag>
              );
            },
          },
          {
            title: "Tự động áp dụng",
            dataIndex: "auto_apply_for_user",
            key: "auto_apply_for_user",
            width: 100,
            render(value, record, index) {
              return <Tag color={value ? "#108ee9" : "#f50"}>{value ? "Tự động" : "Không"}</Tag>;
            },
          },
          {
            title: "Thumnail",
            dataIndex: "thumbnail",
            key: "thumbnail",
            width: 100,
            render(value, record, index) {
              return (
                <Image
                  src={value}
                  alt="No Image"
                  style={{
                    width: "40px",
                    height: "40px",
                  }}
                />
              );
            },
          },
          {
            title: "Tiêu đề",
            dataIndex: "title",
            key: "title",
            width: 200,
          },
          {
            title: "Ngày bắt đầu",
            dataIndex: "sale_start_date",
            key: "sale_start_date",
            width: 100,
            render(value, record, index) {
              if (record.is_recurring) {
                return formatRecurringDays(record.recurring_days);
              }
              return new Date(value).toLocaleDateString("vi-VN");
            },
          },
          {
            title: "Ngày kết thúc",
            dataIndex: "sale_end_date",
            key: "sale_end_date",
            width: 100,
            render(value, record, index) {
              if (record.is_recurring) {
                return formatRecurringDays(record.recurring_days);
              }
              return new Date(value).toLocaleDateString("vi-VN");
            },
          },
          {
            title: "Loại KM",
            dataIndex: "sale_type",
            key: "sale_type",
            minWidth: 120,
            render(value, record, index) {
              return value === SALE_TYPE_ENUM.ALL_BILL
                ? "KM trên tổng đơn hàng"
                : value === SALE_TYPE_ENUM.EACH_ITEM
                ? "KM cho từng sản phẩm"
                : value === SALE_TYPE_ENUM.NEW_USER
                ? "KM cho đơn onl mới"
                : "KM cho combo";
            },
          },
          {
            title: "Giá trị KM",
            dataIndex: "sale_value",
            key: "sale_value",
            minWidth: 100,
            render(value, record, index) {
              return (
                value?.toLocaleString() +
                ` ${
                  SALE_VALUE_TYPE_OPTION?.find((op) => op.value === record.sale_value_type)?.label
                }`
              );
            },
          },
          {
            title: "KM tối đa",
            dataIndex: "max_discount_value",
            key: "max_discount_value",
            minWidth: 100,
            render(value, record, index) {
              return (value?.toLocaleString() ?? 0) + " VNĐ";
            },
          },
          {
            title: "Điều kiện",
            dataIndex: "sale_order_condition",
            key: "sale_order_condition",
            minWidth: 100,
            render(value, record, index) {
              return value === null
                ? ""
                : record.sale_type === SALE_TYPE_ENUM.ALL_BILL
                ? `>= ${value?.toLocaleString("vi-VN")} VND`
                : `>= ${value?.toLocaleString("vi-VN")} đơn vị sp`;
            },
          },
          {
            title: "Áp dụng cho SL user",
            dataIndex: "applicable_user_count",
            key: "applicable_user_count",
            minWidth: 100,
            render(value, record, index) {
              if (value && value > 0) {
                return `${record.applied_user_count} / ${record.applicable_user_count}`;
              } else {
                return "N/A";
              }
            },
          },
          {
            title: "Giá mới (tạm thời)",
            dataIndex: "new_price_temp",
            key: "new_price_temp",
            width: 180,
          },
          {
            title: "Áp dụng cho mặt hàng",
            dataIndex: "apply_for",
            key: "apply_for",
            width: 300,
            minWidth: 300,
            render(value, record, index) {
              return record.sale_type !== SALE_TYPE_ENUM.COMBO
                ? isEmpty(value)
                  ? ""
                  : items
                      ?.filter((e) => value.includes(e.id))
                      .map((e) => e.name)
                      .join("  ,   ")
                : items
                    ?.filter((e) => record.combo_items?.map((t) => t.item_id)?.includes(e.id))
                    ?.map((ef) => ef.name)
                    ?.join(",");
            },
          },
          {
            title: "Nội dung",
            dataIndex: "content",
            key: "content",
            width: 400,
            minWidth: 300,
          },
        ]}
        rowKey={"id"}
        rowSelection={{
          type: "radio",
          selectedRowKeys: selectedItem?.id ? [selectedItem.id] : [],
          onSelect(record) {
            setSelectedItem(record);
          },
        }}
        loading={isLoading}
      />
      {createModal}
      {confirmDeleteDialog}
    </div>
  );
}
export default saleInfo;
